import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";

import { ButtonV1 } from "components";
import DateRangePicker from "./DateRangePicker";

import UploadFilesTable from "./UploadFilesTable";
import { postCall_v2 } from "services";
import { formatDateToAPI } from "../DataModel";
import useDownload from "hooks/useDownload";

const UploadFilesView = ({ onClose, rowsPerPage }) => {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [page, setPage] = useState(0);

  const { downloadDocument } = useDownload();

  const fetchUploadedFiles = async (pageNum, fromDate, toDate) => {
    try {
      setIsLoading(true);

      const payload = {
        offset: pageNum * rowsPerPage,
        limit: rowsPerPage,
        dateFrom: formatDateToAPI(fromDate),
        dateTo: formatDateToAPI(toDate),
      };

      const res = await postCall_v2(
        `/joms/api/external/v1/inventory-data/mto-mts/uploads/history`,
        payload,
      );

      setData(res?.data?.data?.files || []);
      setTotalCount(res?.data?.data?.summary?.totalFiles ?? 0);
      setSummary(res?.data?.data?.summary || null);
    } catch {
      setData([]);
      setSummary(null);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (fileId, fileName) => {
    await downloadDocument(
      `/joms/api/external/v1/inventory-data/fetch-mto-mts-sheet-errors/${fileId}`,
      fileName,
    );
  };

  useEffect(() => {
    fetchUploadedFiles(page, fromDate, toDate);
  }, [page]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleSubmit = () => {
    if (isLoading) return;
    if (page === 0) {
      fetchUploadedFiles(0, fromDate, toDate);
    } else {
      setPage(0);
    }
  };

  return (
    <>
      <Typography fontWeight={"bold"} mb={4}>
        All Files
      </Typography>

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography>
          {summary && data?.length ? (
            <>
              <strong>{`${summary.failedFiles} out of ${summary.totalFiles}`}</strong>
              {" records failed. Review details below"}
            </>
          ) : (
            ""
          )}
        </Typography>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onFromChange={setFromDate}
            onToChange={setToDate}
          />
          <ButtonV1
            title="Submit"
            onClick={handleSubmit}
            disabled={!fromDate && !toDate}
          />
        </Box>
      </Box>

      <UploadFilesTable
        data={data}
        isLoading={isLoading}
        page={page}
        count={totalCount}
        onPageChange={handlePageChange}
        onDownload={handleDownload}
        rowsPerPage={rowsPerPage}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          marginTop: 4,
          paddingTop: 3,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <ButtonV1
          variant="outlined"
          color="secondary"
          title="Close"
          size="small"
          onClick={onClose}
        />
      </Box>
    </>
  );
};

export default UploadFilesView;
